import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Reveal from "../components/Reveal";
import { journalPage } from "../mock";
import { IMG_DIMS } from "../imgDims";

const JournalArticle = () => {
  const { slug } = useParams();
  const entries = [...journalPage.entries].sort((a, b) =>
    (b.date || "").localeCompare(a.date || "")
  );
  const index = entries.findIndex((e) => e.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (index === -1) return <Navigate to="/journal" replace />;

  const entry = entries[index];
  const next = entries[(index + 1) % entries.length];

  return (
    <div id="top" className="min-h-screen flex flex-col bg-background-ivory">
      <Header />

      <main className="flex-grow pt-32 pb-24 px-margin-mobile md:px-margin-desktop">
        <article className="max-w-2xl mx-auto" data-testid="journal-article">
          <Reveal>
            <Link
              to="/journal"
              className="inline-flex items-center gap-2 font-inter text-label-sm uppercase tracking-widest text-brand-secondary hover:text-brand-ink transition-colors mb-12 group"
              data-testid="journal-article-back"
            >
              <ArrowLeft className="w-4 h-4 transition-transform duration-300 group-hover:-translate-x-1" />
              The Journal
            </Link>
          </Reveal>

          {entry.heroImage && (
            <Reveal className="mb-12 md:mb-16">
              <div className="w-full flex justify-center bg-cover-ivory">
                <img
                  src={entry.heroImage}
                  alt={entry.title}
                  className="max-h-[72vh] w-auto max-w-full object-contain"
                  width={IMG_DIMS[entry.heroImage]?.[0]}
                  height={IMG_DIMS[entry.heroImage]?.[1]}
                  decoding="async"
                  data-testid="journal-article-hero"
                />
              </div>
            </Reveal>
          )}

          <Reveal
            as="p"
            className="font-inter text-eyebrow uppercase tracking-[0.28em] text-brand-secondary mb-6"
          >
            {entry.category}
            {entry.kicker ? ` · ${entry.kicker}` : ""}
          </Reveal>

          <Reveal
            as="h1"
            delay={100}
            className="font-cormorant text-[34px] md:text-[52px] leading-[1.1] text-brand-ink mb-8"
            data-testid="journal-article-title"
          >
            {entry.title}
          </Reveal>

          <Reveal delay={200} className="w-16 h-px bg-brand-gold mb-8" />

          <div className="space-y-4" data-testid="journal-article-body">
            {entry.body.map((para, i) => (
              <Reveal
                as="p"
                key={para}
                delay={250 + i * 60}
                className="font-cormorant text-body-lg text-deep-taupe leading-[1.5]"
              >
                {para}
              </Reveal>
            ))}
          </div>
        </article>

        {entry.gallery && entry.gallery.length > 0 && (
          <div className="max-w-2xl mx-auto mt-8 md:mt-10 space-y-8">
            {entry.gallery.map((src, i) => (
              <Reveal key={src} className="w-full flex justify-center bg-cover-ivory">
                <img
                  src={src}
                  alt={`${entry.title} — ${i + 2}`}
                  className="max-h-[72vh] w-auto max-w-full object-contain"
                  width={IMG_DIMS[src]?.[0]}
                  height={IMG_DIMS[src]?.[1]}
                  loading="lazy"
                  decoding="async"
                  data-testid={`journal-article-gallery-${i}`}
                />
              </Reveal>
            ))}
          </div>
        )}

        {/* Next note */}
        <div className="max-w-2xl mx-auto mt-20 pt-10 border-t border-hairline-beige">
          <p className="font-inter text-eyebrow uppercase tracking-widest text-brand-secondary mb-4">
            The Next Note
          </p>
          <Link
            to={`/journal/${next.slug}`}
            className="group inline-flex items-baseline gap-3"
            data-testid="journal-article-next"
          >
            <h3 className="font-cormorant text-headline-md text-brand-ink transition-colors duration-300 group-hover:text-brand-gold">
              {next.title}
            </h3>
            <ArrowRight className="w-5 h-5 shrink-0 self-center text-brand-secondary transition-transform duration-300 group-hover:translate-x-1 group-hover:text-brand-gold" />
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default JournalArticle;
